import React, { useRef, useMemo, useState, useEffect } from 'react'
import { History } from 'history'
import { useDispatch, useSelector } from 'react-redux'

import Layout from '../components/layout'
import Intro from '../components/intro'
import WelcomeChecklist from '../components/welcome-checklist'
import WelcomeRightModule from '../components/welcome-rhs'
import styles from '../component-styles/welcome-page.module.scss'
import SiteWrapper from '../components/sitewrapper'
import ProgressBar from '../components/progress-bar'
import useOnboarding from '../hooks/useOnboarding'
import TwoColumns, { Column } from '../components/two-columns'
import useMobile from '../hooks/useMobile'
import { GraphQLState } from '../store/graphql-reducer'
import { SiteState } from '../store/site-reducer'
import { SupportState } from '../store/support-reducer'
import { timeOfDayGreeting } from '../helpers/dates'
import trophy from '../svgs/trophy.svg'
import quicklink from '../svgs/quicklink.svg'
import ToggleBox from '../components/toggle-box'
import Button from '../components/button'
import { logActionAsync } from '../store/actions'
import onboardingSectionData from '../static-copy/onboarding-section-data.json'
import { BulletCounter } from '../components/counter'
import { Preloader } from '../components/loader'

export const QuickAccess = (): React.ReactElement => {
  const [quickLinksOpenState, setQuickLinksOpenState] = useState(true)

  const {
    graphql: { currentUser },
    support: { pendingRequests },
    site: { selected },
  }: {
    graphql: GraphQLState
    support: SupportState
    site: SiteState
  } = useSelector((s: any): any => s)

  const userPermission = currentUser?.currentAccountPermission || ''

  return (
    <>
      <ToggleBox
        noPadding
        className={styles.quickAccess}
        disableToggle={!selected || !currentUser}
        heading={
          <div className={styles.boxHeader}>
            <img src={quicklink} alt="Quick access" />
            <h2>Quick access</h2>
            {(!selected || !currentUser) && (
              <Preloader style={{ width: 30, height: 20, margin: 0 }} />
            )}
          </div>
        }
        open={quickLinksOpenState}
        onToggle={(state: boolean) => {
          setQuickLinksOpenState(state)
        }}
      >
        {selected && currentUser && (
          <div className={styles.quickAccessButtons}>
            {selected.trackAvailable && (
              <Button
                type="selectBox"
                to="/track/create-campaign-codes"
                className={styles.quickAccessButton}
              >
                <div className={styles.boxHeader}>
                  <div className={styles.emoji}>
                    <span role="img" aria-label="Enterprise client details">
                      🔗
                    </span>
                  </div>{' '}
                  Create a new campaign link
                </div>
              </Button>
            )}
            {selected.trackAvailable && (
              <Button
                type="selectBox"
                to="/track/view-campaign-codes"
                className={styles.quickAccessButton}
              >
                <div className={styles.boxHeader}>
                  <div className={styles.emoji}>
                    <span role="img" aria-label="Enterprise client details">
                      🔗
                    </span>
                  </div>{' '}
                  View all campaign links
                </div>
              </Button>
            )}
            {selected.trackAvailable && (
              <Button
                type="selectBox"
                to="/report/performance"
                className={styles.quickAccessButton}
              >
                <div className={styles.boxHeader}>
                  <div className={styles.emoji}>
                    <span role="img" aria-label="Enterprise client details">
                      🔗
                    </span>
                  </div>{' '}
                  Create a new performance report
                </div>
              </Button>
            )}
            {((selected.trackAvailable && userPermission === 'admin') ||
              userPermission === 'support') &&
              pendingRequests.length > 0 && (
                <Button
                  type="selectBox"
                  to="track/edit-campaign-codes"
                  className={styles.quickAccessButton}
                >
                  <div className={styles.boxHeader}>
                    <div className={styles.emoji}>
                      <span role="img" aria-label="Enterprise client details">
                        🔗
                      </span>
                    </div>{' '}
                    Approve or add new parameter dropdowns
                    <BulletCounter
                      count={pendingRequests.length}
                      className={styles.notificationBullet}
                    />
                  </div>
                </Button>
              )}
          </div>
        )}
      </ToggleBox>
    </>
  )
}

interface WelcomePageProps {
  history: History
}

export default function WelcomePage({
  history,
}: WelcomePageProps): React.ReactElement {
  const dispatch = useDispatch()

  // Handle desktop/mobile layout
  const isMobileMenu = useMobile(1200)
  const fixedColumnRef = useRef<HTMLDivElement>(null)
  const mainColumnRef = useRef<HTMLDivElement>(null)
  const checklistRef = useRef<HTMLDivElement>(null)

  const [loading, setLoading] = useState(true)
  const [onboardingOpenState, setOnboardingOpenState] = useState(true)

  const { fullOnboardingSections, setFullOnboardingSections } = useOnboarding()

  const [completedSections, completedPercent] = useMemo(() => {
    if (fullOnboardingSections) {
      const _completedSections = fullOnboardingSections.filter(
        // TODO: Update to reflect new skip logic
        (section) => {
          return (
            (section.sectionCompleted &&
              (section.sectionSkipped || !section.sectionSkippedBefore)) ||
            section.sectionSkipped
          )
        },
      ).length
      const _completedPercent = Math.ceil(
        (_completedSections / fullOnboardingSections.length) * 100,
      )
      if (_completedPercent === 100) {
        setOnboardingOpenState(false)
      }
      setLoading(false)
      return [_completedSections, _completedPercent]
    }
    return [null, 0]
  }, [fullOnboardingSections])

  const {
    graphql: { currentUser },
  }: {
    graphql: GraphQLState
  } = useSelector((s: any): any => s)

  const { firstName } = currentUser || {
    firstName: '',
  }

  const [welcomeName, setWelcomeName] = useState(
    `${timeOfDayGreeting(new Date())}`,
  )

  useEffect(() => {
    if (firstName) {
      setWelcomeName(`${timeOfDayGreeting(new Date(Date.now()))} ${firstName}`)
    }
  }, [firstName])

  return (
    <SiteWrapper>
      <Layout>
        <Intro title={welcomeName} />
        <TwoColumns>
          <Column main fwdRef={mainColumnRef}>
            <>
              <div className={styles.container}>
                {(completedPercent === 100 || loading) && (
                  <div className={styles.containerInner}>
                    <QuickAccess />
                  </div>
                )}
                <div className={styles.containerInner}>
                  <ToggleBox
                    noPadding
                    className={styles.onboardingToggleBox}
                    disableToggle={loading}
                    heading={
                      <>
                        <div className={styles.boxHeader}>
                          {completedPercent === 100 ? (
                            <img src={trophy} alt="Complete" />
                          ) : (
                            <div className={styles.emoji}>
                              <span
                                role="img"
                                aria-label="Enterprise client details"
                              >
                                📝
                              </span>
                            </div>
                          )}
                          <h2>Onboarding checklist</h2>
                          {loading && (
                            <Preloader
                              style={{ width: 30, height: 20, margin: 0 }}
                            />
                          )}
                          {!loading &&
                            !onboardingOpenState &&
                            fullOnboardingSections &&
                            completedSections !== null && (
                              <div className={styles.collapsedProgressBar}>
                                <ProgressBar
                                  percentage={completedPercent}
                                  showPercent={false}
                                  thin
                                  superThin
                                  green
                                />
                                <p>{completedPercent}%</p>
                              </div>
                            )}
                        </div>
                        {onboardingOpenState &&
                          fullOnboardingSections &&
                          completedSections !== null && (
                            <>
                              {completedPercent < 100 && (
                                <p style={{ margin: '8px 0' }}>
                                  This is your personal guide for making the
                                  most of Uplifter. Let's get you set up and
                                  creating links!
                                </p>
                              )}
                              <p>
                                You've completed{' '}
                                {completedPercent === 100 ? (
                                  'all'
                                ) : (
                                  <>
                                    <strong>
                                      {completedSections.toString()}
                                    </strong>{' '}
                                    out of{' '}
                                    <strong>
                                      {fullOnboardingSections.length.toString()}
                                    </strong>
                                  </>
                                )}{' '}
                                steps
                                {completedPercent === 100
                                  ? ' - congrats!'
                                  : '.'}
                              </p>
                              <ProgressBar
                                percentage={completedPercent}
                                showPercent={false}
                                thin
                                green
                              />
                            </>
                          )}
                      </>
                    }
                    open={onboardingOpenState}
                    onToggle={(state: boolean) => {
                      setOnboardingOpenState(state)
                    }}
                  >
                    {fullOnboardingSections && completedSections !== null && (
                      <>
                        <WelcomeChecklist
                          fwdRef={checklistRef}
                          history={history}
                          fullOnboardingSections={fullOnboardingSections}
                          setFullOnboardingSections={setFullOnboardingSections}
                        />
                        {completedPercent < 100 && (
                          <Button
                            className={styles.skipAll}
                            type="text"
                            onClick={() => {
                              const copy: OnboardingSection[] = JSON.parse(
                                JSON.stringify(fullOnboardingSections),
                              )
                              copy.forEach((_, index) => {
                                if (!copy[index].sectionSkipped) {
                                  copy[index].sectionSkipped = true
                                  copy[index].sectionSkippedBefore = true

                                  dispatch(
                                    logActionAsync(
                                      onboardingSectionData[
                                        copy[index].onboardingSectionID
                                      ].skipAction,
                                    ),
                                  )
                                }
                              })

                              setFullOnboardingSections(copy)
                              setOnboardingOpenState(false)
                            }}
                          >
                            <em>I'm an expert - don't show me these steps</em>
                          </Button>
                        )}
                      </>
                    )}
                  </ToggleBox>
                </div>
                {completedPercent < 100 && !loading && (
                  <div className={styles.containerInner}>
                    <QuickAccess />
                  </div>
                )}
              </div>
            </>
          </Column>
          <Column
            side
            fwdRef={fixedColumnRef}
            className={styles.rightColumn}
            style={{
              top: `${isMobileMenu ? 70 : 10}px`,
            }}
          >
            <WelcomeRightModule />
          </Column>
        </TwoColumns>
      </Layout>
    </SiteWrapper>
  )
}
